import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  ConfigProvider,
  Dropdown,
  Flex,
  Modal,
  Space,
  Tooltip,
  Typography,
  message,
  type MenuProps,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_BULK } from "@/services/XCONS_ENV";
import { ESTADOS, estadoDelHit } from "./estados";
import { esBorrador } from "./types";
import type { BulkAction, BulkResult, CatalogHit, DraftRow } from "./types";
import type { EstadoPublicado } from "./estados";
import {
  asignarDuenoPara,
  cuantasAdmiten,
  desgloseDeEliminacion,
  desgloseDePendientes,
  desgloseDeTransicion,
  seCortoPorTiempo,
  tiempoDelLote,
} from "./acciones-en-lote";
import { AsignarDueno } from "./AsignarDueno";
import { TEMA_MENU } from "./ProductStateSteps";

const { Text } = Typography;

/** Los destinos que ofrece la barra. La matriz real la valida el backend, ítem por ítem. */
const DESTINOS: Array<{
  to: EstadoPublicado;
  label: string;
  /** La acción de `bulkActions` que la habilita (xcons-docs#430). */
  accion: BulkAction;
  /** El confirm simple: sólo la pregunta (xcons-docs#462). Sin ella, el desglose. */
  pregunta?: string;
  /** El aviso cuando sale bien (xcons-docs#487). */
  aviso: (n: number) => string;
}> = [
  {
    to: "active",
    label: "Publicar",
    accion: "publish",
    aviso: (n) =>
      n === 1 ? "Se publicó el producto" : `Se publicaron ${n} productos`,
  },
  {
    to: "inactive",
    label: "Pausar",
    accion: "pause",
    pregunta: "¿Estás seguro que querés pausar estos productos?",
    aviso: (n) =>
      n === 1 ? "Se pausó el producto" : `Se pausaron ${n} productos`,
  },
  {
    to: "archived",
    label: "Archivar",
    accion: "archive",
    pregunta: "¿Estás seguro que querés archivar estos productos?",
    aviso: (n) =>
      n === 1 ? "Se archivó el producto" : `Se archivaron ${n} productos`,
  },
];

/**
 * Acciones masivas del listado del catálogo (PIM Fase 21). Se sacaron con el rediseño del
 * listado y vuelven con xcons-docs#433: una importación puede dejar cientos de productos
 * esperando, y aprobarlos de a uno no es viable.
 *
 * **Qué se ofrece lo dice cada fila** (`bulkActions`, xcons-docs#430): las acciones van en
 * un desplegable (xcons-docs#462), una transición se habilita si alguna fila de la selección
 * la admite, y Asignar dueño se muestra deshabilitado y explicado si alguna no. Con un API
 * anterior a #430 se ofrece todo, sin Asignar dueño.
 *
 * **El confirm dice cuántos ítems de cada tipo abarca, y eso es el punto de la pantalla.**
 * "Publicar 300 productos" no es una información útil: lo que el operador necesita saber es
 * que 180 ya están publicados y no cambian, 90 van a publicarse y 30 no cumplen CON-P001.
 * El desglose que se puede hacer *antes* de mandar sale del estado que ya trae el listado; lo
 * que no se pudo aplicar lo cuenta el aviso al terminar.
 *
 * **Se manda sólo lo que admite la acción** (xcons-docs#453), y el confirm dice por qué no
 * se toca el resto: ya está así, desde ese estado no se puede, o falta permiso. Mandarlo
 * todo lo hacía volver como error —"3 con error" por publicar lo que ya estaba activo—.
 *
 * **El lote no es atómico y la UI lo asume:** vuelve un reporte por ítem, y el aviso dice
 * cuántos se aplicaron y cuántos no (sin ventana de resultado desde xcons-docs#499).
 */
export const BulkActions: React.FC<{
  seleccion: Array<CatalogHit | DraftRow>;
  onLimpiar: () => void;
  onListo: () => void;
}> = ({ seleccion, onLimpiar, onListo }) => {
  const [ejecutando, setEjecutando] = useState(false);
  const [asignando, setAsignando] = useState(false);

  /**
   * **Qué se seleccionó, no en qué pestaña estamos.** Con una sola lista la selección puede
   * mezclar publicados con borradores, y las dos acciones son excluyentes: las transiciones
   * corren sobre productos de Magento y la aprobación sobre versiones que todavía no
   * existen ahí. Mezclado, no se ofrece ninguna: la alternativa es un lote que falla a la
   * mitad y deja al operador sin saber qué se aplicó.
   */
  const soloPublicados =
    seleccion.length > 0 && seleccion.every((r) => !esBorrador(r));
  const soloSinPublicar =
    seleccion.length > 0 && seleccion.every((r) => esBorrador(r));

  /** El desglose por estado actual, que es lo que hace honesto al confirm. */
  const porEstado = useMemo(() => {
    if (!soloPublicados) return null;
    const out = new Map<string, number>();
    for (const row of seleccion as CatalogHit[]) {
      // Por `state` y no por `status`: con `status` un archivado se contaba como pausado
      // (#380). Es la misma etiqueta que la columna Estado.
      const clave = ESTADOS[estadoDelHit(row)].label;
      out.set(clave, (out.get(clave) ?? 0) + 1);
    }
    return [...out.entries()];
  }, [seleccion, soloPublicados]);

  /**
   * Sobre lo sin publicar hay dos acciones, y cada una toma lo suyo de la selección
   * (xcons-docs#453): **aprobar** lo que está en revisión y **mandar a revisión** los
   * borradores. Cada botón aparece si al menos una fila lo admite; `null` es el API sin el
   * contrato de #430, y ahí aprobar se ofrece como antes y mandar a revisión no.
   */
  const aprobables = soloSinPublicar ? cuantasAdmiten(seleccion, "approve") : null;
  const mandables = soloSinPublicar ? cuantasAdmiten(seleccion, "submit") : null;

  /**
   * Eliminar borradores (xcons-docs#473) vale sobre **cualquier** selección: elimina los
   * borradores de alta y deja afuera el resto, diciendo por qué. `null` es el API sin el
   * contrato de #430, que tampoco tiene `bulk/discard`: ahí no se ofrece.
   */
  // Se cuenta lo que de verdad se manda, no cualquier fila con `discard`: si no, el botón
  // podía quedar habilitado para un "Eliminar 0 borradores".
  const eliminables =
    seleccion.length && cuantasAdmiten(seleccion, "discard") !== null
      ? desgloseDeEliminacion(seleccion).seTocan.length
      : null;
  const hayBorradores = seleccion.some(
    (r) => esBorrador(r) && r.state === "draft",
  );

  const dueno = soloPublicados
    ? asignarDuenoPara(seleccion)
    : { estado: "oculto" as const };

  /**
   * **El aviso de arriba alcanza, sin reporte** (xcons-docs#462, #487, y sin excepción desde
   * xcons-docs#499): dice qué se hizo, y si algún producto no se pudo, cuántos.
   */
  const ejecutar = async (
    url: string,
    body: Record<string, unknown>,
    aviso: (n: number) => string,
  ) => {
    setEjecutando(true);
    const ids = (body.productIds ?? body.pendingIds ?? []) as unknown[];
    try {
      const { data } = await axiosInstance.post<BulkResult>(url, body, {
        timeout: tiempoDelLote(url.split("/").pop() ?? "", ids.length),
      });
      if (data.failed === 0) {
        message.success(aviso(data.ok));
      } else {
        message.warning(
          `${data.ok ? aviso(data.ok) : "No se aplicó a ninguno"}. ${data.failed} con error`,
        );
      }
      onListo();
      return true;
    } catch (e) {
      if (seCortoPorTiempo(e)) {
        // No es un error: el API aplica el lote aunque el admin deje de esperar.
        message.warning(
          "El lote está tardando más de lo esperado y puede seguir aplicándose. Recargá el listado en unos minutos para ver el resultado",
          10,
        );
        onListo();
        return true;
      }
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo ejecutar el lote",
      );
      return false;
    } finally {
      setEjecutando(false);
    }
  };

  const transicionar = (destino: (typeof DESTINOS)[number]) => {
    const { seTocan, noSeTocan } = desgloseDeTransicion(
      seleccion as CatalogHit[],
      destino.accion,
      destino.to,
    );
    const ids = seTocan.map((r) => r.magentoProductId).filter(Boolean);

    Modal.confirm({
      title: `${destino.label} ${ids.length} ${ids.length === 1 ? "producto" : "productos"}`,
      width: 520,
      content: destino.pregunta ? (
        // Pausar y Archivar preguntan y nada más (xcons-docs#462): cuántos no se pudieron
        // aplicar lo dice el aviso.
        <Flex vertical gap={4}>
          <Text>{destino.pregunta}</Text>
          {destino.to === "archived" && (
            // Archivar descarta la versión de edición que tenga encima (R14): lo que se
            // estaba editando no se recupera.
            <Text type="secondary">
              Si alguno tiene una versión de edición, se descarta
            </Text>
          )}
        </Flex>
      ) : (
        <Flex vertical gap={8}>
          {porEstado && porEstado.length > 0 && (
            <div>
              <Text type="secondary">De lo seleccionado:</Text>
              <ul style={{ margin: "4px 0 0 16px" }}>
                {porEstado.map(([clave, n]) => (
                  <li key={clave}>
                    {n} {n === 1 ? "producto" : "productos"} en {clave}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {noSeTocan.length > 0 && (
            <NoSeTocan grupos={noSeTocan} />
          )}
          <Text type="secondary">
            Cada producto pasa por su propio control: publicar exige cumplir CON-P001.
            Los que no puedan no frenan a los demás.
          </Text>
        </Flex>
      ),
      okText: destino.label,
      cancelText: "Cancelar",
      onOk: () =>
        ejecutar(
          `${API_URL_PIM_BULK}/transition`,
          { productIds: ids, to: destino.to },
          destino.aviso,
        ),
    });
  };

  const aprobar = () => {
    const { seTocan, noSeTocan } = desgloseDePendientes(
      seleccion as DraftRow[],
      "approve",
    );
    // `id` viaja como string (es un `bigint` en la base): se coerciona acá.
    const ids = seTocan.map((r) => Number(r.id));

    Modal.confirm({
      title: `Aprobar ${ids.length} ${ids.length === 1 ? "versión" : "versiones"}`,
      width: 560,
      content: (
        <Flex vertical gap={8}>
          {noSeTocan.length > 0 && <NoSeTocan grupos={noSeTocan} />}
        </Flex>
      ),
      okText: "Aprobar",
      cancelText: "Cancelar",
      onOk: () =>
        ejecutar(
          `${API_URL_PIM_BULK}/approve`,
          { pendingIds: ids },
          (n) =>
            n === 1 ? "Se aprobó el producto" : `Se aprobaron ${n} productos`,
        ),
    });
  };

  /**
   * Mandar a revisión en lote (xcons-docs#453): lo que deja el importador de a miles. Cada
   * uno pasa por CON-P001, y el que no cumple sigue como borrador.
   */
  const mandarARevision = () => {
    const { seTocan, noSeTocan } = desgloseDePendientes(
      seleccion as DraftRow[],
      "submit",
    );
    const ids = seTocan.map((r) => Number(r.id));

    Modal.confirm({
      title: `Mandar a revisión ${ids.length} ${ids.length === 1 ? "borrador" : "borradores"}`,
      width: 560,
      content: (
        <Flex vertical gap={8}>
          {noSeTocan.length > 0 && <NoSeTocan grupos={noSeTocan} />}
          <Text type="secondary">
            Cada uno pasa por CON-P001. Los que no cumplen siguen como borrador; los demás
            pasan a revisión y esperan que un Admin los apruebe.
          </Text>
        </Flex>
      ),
      okText: "Mandar a revisión",
      cancelText: "Cancelar",
      onOk: () =>
        ejecutar(
          `${API_URL_PIM_BULK}/submit`,
          { pendingIds: ids },
          (n) =>
            n === 1
              ? "Se mandó a revisión el producto"
              : `Se mandaron a revisión ${n} productos`,
        ),
    });
  };

  /**
   * Eliminar borradores en lote (xcons-docs#473): lo que el importador deja de a cientos y no
   * sirve, y los cambios rechazados de productos publicados. Se mandan sólo los borradores; el
   * confirm dice qué queda afuera y por qué.
   */
  const eliminarBorradores = () => {
    const { seTocan, noSeTocan } = desgloseDeEliminacion(seleccion);
    const ids = seTocan.map((r) => Number(r.id));
    const ediciones = seTocan.filter((r) => r.productId).length;

    Modal.confirm({
      title: `Eliminar ${ids.length} ${ids.length === 1 ? "borrador" : "borradores"}`,
      width: 560,
      content: (
        <Flex vertical gap={8}>
          <Text>¿Estás seguro que querés eliminar estos borradores?</Text>
          {ediciones > 0 && (
            <Text type="secondary">
              {ediciones === 1
                ? "Uno es un cambio sobre un producto publicado: se descarta el cambio y el producto sigue publicado como está."
                : `${ediciones} son cambios sobre productos publicados: se descartan los cambios y los productos siguen publicados como están.`}
            </Text>
          )}
          {noSeTocan.length > 0 && <NoSeTocan grupos={noSeTocan} />}
          <Text type="secondary">No se puede deshacer.</Text>
        </Flex>
      ),
      // El botón primario, verde, no `danger` (xcons-docs#479).
      okText: "Sí, eliminar",
      cancelText: "Cancelar",
      onOk: () =>
        ejecutar(
          `${API_URL_PIM_BULK}/discard`,
          { pendingIds: ids },
          (n) =>
            n === 1 ? "Se eliminó el borrador" : `Se eliminaron ${n} borradores`,
        ),
    });
  };

  const asignarDueno = async (
    userId: number,
    nombre: string,
    level: "admin" | "editor" | "viewer",
  ) => {
    const ids = (seleccion as CatalogHit[])
      .map((r) => r.magentoProductId)
      .filter(Boolean);
    const listo = await ejecutar(
      `${API_URL_PIM_BULK}/owner`,
      { productIds: ids, userId, level },
      (n) =>
        n === 1
          ? `Se agregó a ${nombre} en el producto`
          : `Se agregó a ${nombre} en ${n} productos`,
    );
    // Si falló entero —`OWNER_NOT_ELIGIBLE`, por ejemplo—, el modal queda abierto para
    // elegir a otra persona.
    if (listo) setAsignando(false);
  };

  /**
   * Lo que ofrece el desplegable. **Se ve todo lo que corresponde a la selección, y se
   * habilita lo que alguna fila admite** (xcons-docs#462): antes una transición que ninguna
   * fila admitía no aparecía. Sin el contrato de #430 (`null`) todo queda habilitado.
   */
  const acciones: NonNullable<MenuProps["items"]> = [];
  /**
   * Una selección que mezcla publicados con borradores no admite ninguna acción de las dos
   * listas: **se ven todas, deshabilitadas**, en vez de un texto que lo explique
   * (xcons-docs#499). Sólo Eliminar borradores vale para las dos.
   */
  const mezclada = !soloPublicados && !soloSinPublicar;
  if (soloPublicados || mezclada) {
    for (const d of DESTINOS) {
      acciones.push({
        key: d.to,
        label: d.label,
        disabled: mezclada || cuantasAdmiten(seleccion, d.accion) === 0,
        onClick: () => transicionar(d),
      });
    }
  }
  // **Siempre visible** (xcons-docs#479): cuando no se puede, en gris y con el motivo al
  // pasar el mouse, no escondida. Con un solo producto sin permiso adentro, el lote volvería
  // con ese ítem en `PRODUCT_ADMIN_REQUIRED`.
  if (seleccion.length) {
    const motivoSinParticipantes = !soloPublicados
      ? "Sólo para productos publicados: un borrador todavía no tiene participantes"
      : // El motivo de permiso, sin detalle de niveles (xcons-docs#479).
        dueno.estado !== "habilitado"
        ? "No tenés permisos"
        : null;
    acciones.push({
      key: "dueno",
      label: motivoSinParticipantes ? (
        <Tooltip placement="right" title={motivoSinParticipantes}>
          Agregar participantes
        </Tooltip>
      ) : (
        "Agregar participantes"
      ),
      disabled: motivoSinParticipantes !== null,
      onClick: () => setAsignando(true),
    });
  }
  // Lo sin publicar tiene dos acciones, y cada una toma lo suyo de la selección
  // (xcons-docs#453): el confirm dice qué queda afuera y por qué. Sin el contrato de #430
  // (`null`), Aprobar se ofrece como antes y Mandar a revisión no.
  if (soloSinPublicar || mezclada) {
    if (mezclada || mandables !== null) {
      acciones.push({
        key: "submit",
        label: "Mandar a revisión",
        disabled: mezclada || mandables === 0,
        onClick: mandarARevision,
      });
    }
    acciones.push({
      key: "approve",
      label: "Aprobar",
      disabled: mezclada || aprobables === 0,
      onClick: aprobar,
    });
  }

  // Al final y para cualquier selección (xcons-docs#473). En gris si ninguna fila lo admite:
  // con borradores adentro es por permiso, y el motivo se dice. En negro como las
  // demás, no en rojo (xcons-docs#479).
  if (eliminables !== null) {
    const sinPermiso = eliminables === 0 && hayBorradores;
    acciones.push({
      key: "discard",
      label: sinPermiso ? (
        <Tooltip
          placement="right"
          title="No tenés permisos"
        >
          Eliminar borradores
        </Tooltip>
      ) : (
        "Eliminar borradores"
      ),
      disabled: eliminables === 0,
      onClick: eliminarBorradores,
    });
  }

  if (!seleccion.length) return null;

  return (
    <>
      <Alert
        type="info"
        message={
          // Todo junto a la cantidad, no repartido a los costados (xcons-docs#462).
          <Flex align="center" gap={12} wrap="wrap">
            <Text strong>
              {seleccion.length}{" "}
              {seleccion.length === 1 ? "seleccionado" : "seleccionados"}
            </Text>
            <Space wrap>
              {acciones.length > 0 && (
                // Las acciones en un desplegable (xcons-docs#462). Las que la selección no
                // admite se ven deshabilitadas.
                // Con el hover verde claro del menú de la ficha (xcons-docs#479).
                <ConfigProvider theme={TEMA_MENU}>
                  <Dropdown
                    trigger={["click"]}
                    disabled={ejecutando}
                    menu={{ items: acciones }}
                  >
                    <Button loading={ejecutando}>
                      <Space size={4}>
                        Acciones
                        <DownOutlined />
                      </Space>
                    </Button>
                  </Dropdown>
                </ConfigProvider>
              )}
              <Button type="link" onClick={onLimpiar}>
                Deseleccionar
              </Button>
            </Space>
          </Flex>
        }
      />

      <AsignarDueno
        open={asignando}
        onCancelar={() => setAsignando(false)}
        onConfirmar={asignarDueno}
      />
    </>
  );
};

/**
 * Lo que la acción **no** va a tocar, agrupado por motivo, para el confirm (xcons-docs#453):
 * "2 ya están activos · 1 archivados: primero se pasan a Pausado".
 */
const NoSeTocan: React.FC<{ grupos: Array<{ motivo: string; n: number }> }> = ({
  grupos,
}) => (
  <div>
    <Text type="secondary">No se tocan:</Text>
    <ul style={{ margin: "4px 0 0 16px" }}>
      {grupos.map(({ motivo, n }) => (
        <li key={motivo}>
          {n} {motivo}
        </li>
      ))}
    </ul>
  </div>
);
